import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { CheckCircle, CurrencyInr, Copy, Hourglass, FilePdf, Buildings, Bank, Wallet } from "@phosphor-icons/react";

/**
 * Public payment page reached via the shareable /pay/:token URL.
 * Supports three payment methods: UPI, Bank Transfer, Cash (in-person).
 */
export default function PublicPayment() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("upi"); // upi | bank_transfer | cash
  const [utr, setUtr] = useState("");
  const [bankTxn, setBankTxn] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = () => {
    setLoading(true);
    api.get(`/payments/${token}`)
      .then((r) => {
        setData(r.data);
        // Auto-pick the first enabled method on first load
        const enabled = [
          r.data?.upi?.vpa ? "upi" : null,
          r.data?.bank?.enabled ? "bank_transfer" : null,
          r.data?.cash?.enabled ? "cash" : null,
        ].filter(Boolean);
        if (enabled.length) setMethod((m) => (enabled.includes(m) ? m : enabled[0]));
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(fetchData, [token]);

  const upiLink = useMemo(() => {
    if (!data?.upi?.vpa) return "";
    const installments = data?.installments || [];
    const nextInstallment = installments.find((i) => i.status !== "verified");
    const amt = installments.length > 0 ? (nextInstallment?.amount || 0) : data.amount;
    if (!amt) return "";
    const params = new URLSearchParams({
      pa: data.upi.vpa,
      pn: data.upi.payee_name || "Registrar",
      am: String(Number(amt).toFixed(2)),
      cu: "INR",
      tn: `Tuition ${data.applicant?.student_id || ""}${nextInstallment ? ` Inst-${nextInstallment.number}` : ""}`.trim(),
    });
    return `upi://pay?${params.toString()}`;
  }, [data]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { method, note };
      if (method === "upi") payload.utr = utr;
      else if (method === "bank_transfer") payload.bank_txn_number = bankTxn;
      // For cash we just signal a claim — no UTR required
      await api.post(`/payments/${token}/mark-paid`, payload);
      const msg =
        method === "cash"
          ? "Cash claim recorded. Pay at the counter — staff will verify."
          : "Payment marked. Awaiting verification.";
      toast.success(msg);
      setUtr("");
      setBankTxn("");
      setNote("");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not mark paid");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-slate-400">Loading…</div></div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <div className="font-display text-3xl font-black tracking-tighter mb-2">Payment link invalid</div>
          <div className="text-sm text-slate-500">This payment session does not exist or has been retracted.</div>
        </div>
      </div>
    );
  }

  const verified = data.payment_status === "verified";
  const claimed = data.payment_status === "claimed";
  const installments = data.installments || [];

  const nextInstallment = installments.find((i) => i.status !== "verified");
  const amountForUpi = installments.length > 0 ? (nextInstallment?.amount || 0) : data.amount;

  const upiEnabled = !!data.upi?.vpa;
  const bankEnabled = !!data.bank?.enabled && !!data.bank?.account_number;
  const cashEnabled = !!data.cash?.enabled;
  const noMethodConfigured = !upiEnabled && !bankEnabled && !cashEnabled;

  const methods = [
    upiEnabled && { id: "upi", label: "UPI", icon: <CurrencyInr size={16} weight="duotone" /> },
    bankEnabled && { id: "bank_transfer", label: "Bank Transfer", icon: <Bank size={16} weight="duotone" /> },
    cashEnabled && { id: "cash", label: "Cash at counter", icon: <Wallet size={16} weight="duotone" /> },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-5 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <div className="font-display text-lg font-black tracking-tighter">SMS<span className="text-[var(--sms-primary)]">.</span></div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Tuition payment</div>
          </div>
          <div className="text-right">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">Student ID</div>
            <div className="font-mono-sms text-sm" data-testid="public-payment-student-id">{data.applicant.student_id || "—"}</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8">
        <h1 className="font-display text-4xl font-black tracking-tighter">Hi {data.applicant.full_name?.split(" ")[0] || "there"} 👋</h1>
        <p className="text-slate-600 mt-2">
          Your application for <span className="font-semibold">{data.applicant.course_name || "the programme"}</span> is approved. Complete your tuition payment to enrol.
        </p>

        {/* Amount card */}
        <div className="border border-slate-900 bg-white p-6 mt-6">
          <div className="flex items-end justify-between gap-6">
            <div>
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {installments.length > 0 ? `Next instalment (${nextInstallment ? `#${nextInstallment.number} of ${installments.length}` : "all paid"})` : "Amount due"}
              </div>
              <div className="font-display text-5xl font-black tracking-tighter mt-1" data-testid="public-payment-amount">
                ₹ {Number(amountForUpi || 0).toLocaleString("en-IN")}
              </div>
              {installments.length > 0 && (
                <div className="font-mono-sms text-xs text-slate-500 mt-2">
                  Total fee: ₹ {Number(data.amount || 0).toLocaleString("en-IN")}
                </div>
              )}
              {Number(data.discount_amount || 0) > 0 && (
                <div className="mt-3 text-xs text-slate-500 font-mono-sms space-y-0.5" data-testid="public-discount-summary">
                  <div>Original fee: <span className="line-through">₹ {Number(data.fee_amount || 0).toLocaleString("en-IN")}</span></div>
                  <div className="text-[var(--sms-primary)]">Discount: − ₹ {Number(data.discount_amount).toLocaleString("en-IN")}{data.discount_reason ? ` · ${data.discount_reason}` : ""}</div>
                </div>
              )}
            </div>
            <CurrencyInr size={48} weight="duotone" className="text-[var(--sms-primary)] shrink-0" />
          </div>
          {verified && (
            <div className="mt-4 space-y-3" data-testid="public-payment-verified-block">
              <div className="inline-flex items-center gap-2 px-3 py-2 bg-[var(--sms-success)]/10 border border-[var(--sms-success)]/30 text-[#166534]" data-testid="public-payment-verified">
                <CheckCircle size={16} weight="duotone" /> <span className="font-mono-sms text-xs uppercase tracking-[0.15em]">Verified · Enrolled</span>
              </div>
              <div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/${token}/receipt`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="public-receipt-download"
                  className="sms-btn-primary inline-flex items-center gap-2"
                >
                  <FilePdf size={16} weight="duotone" /> Download PDF receipt
                </a>
              </div>
            </div>
          )}
          {claimed && !verified && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-[var(--sms-warning)]/10 border border-[var(--sms-warning)]/30 text-[#92400E]" data-testid="public-payment-claimed">
              <Hourglass size={16} weight="duotone" />
              <span className="font-mono-sms text-xs uppercase tracking-[0.15em]">
                Awaiting verification · {data.payment_method === "cash" ? "Cash claim recorded" : `Ref ${data.payment_utr}`}
              </span>
            </div>
          )}
        </div>

        {!verified && noMethodConfigured && (
          <div className="border border-amber-300 bg-amber-50 p-5 mt-6 text-sm text-amber-900">
            The registrar hasn't configured any payment method yet. Please contact the office.
          </div>
        )}

        {!verified && methods.length > 0 && (
          <>
            {/* Method selector */}
            <div className="mt-6 flex items-center gap-1 border border-slate-200 inline-flex" data-testid="payment-method-tabs">
              {methods.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethod(m.id)}
                  data-testid={`method-tab-${m.id === "bank_transfer" ? "bank" : m.id}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 font-mono-sms text-[10px] uppercase tracking-[0.2em] ${
                    method === m.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {m.icon} {m.label}
                </button>
              ))}
            </div>

            {/* UPI section */}
            {method === "upi" && upiEnabled && (
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div className="border border-slate-200 bg-white p-6 flex flex-col items-center text-center">
                  <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Scan with any UPI app</div>
                  <div className="bg-white p-2 border border-slate-900">
                    <QRCodeSVG value={upiLink} size={200} level="M" data-testid="upi-qr" />
                  </div>
                  <div className="font-mono-sms text-[11px] mt-3 text-slate-500 break-all max-w-full">
                    {upiLink.slice(0, 80)}…
                  </div>
                </div>
                <div className="border border-slate-200 bg-white p-6">
                  <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Or pay manually</div>
                  <div className="space-y-3">
                    <div>
                      <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">UPI ID</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="font-mono-sms text-base text-slate-900 break-all" data-testid="upi-vpa">{data.upi.vpa}</div>
                        <button onClick={() => copy(data.upi.vpa)} data-testid="copy-vpa" className="text-slate-400 hover:text-[var(--sms-primary)]"><Copy size={14} weight="bold" /></button>
                      </div>
                    </div>
                    <div>
                      <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">Payee name</div>
                      <div className="text-sm mt-1">{data.upi.payee_name || "—"}</div>
                    </div>
                    <div>
                      <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">Reference</div>
                      <div className="font-mono-sms text-xs mt-1">Tuition {data.applicant.student_id || ""}</div>
                    </div>
                    {data.upi.instructions && (
                      <div className="pt-3 border-t border-slate-200 text-sm text-slate-600 whitespace-pre-wrap">{data.upi.instructions}</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bank section */}
            {method === "bank_transfer" && bankEnabled && (
              <div className="border border-slate-200 bg-white p-6 mt-6" data-testid="bank-details-card">
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4 inline-flex items-center gap-2">
                  <Buildings size={14} weight="duotone" /> Bank transfer details (NEFT / IMPS / RTGS)
                </div>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <BankRow label="Account name" value={data.bank.account_name} onCopy={copy} />
                  <BankRow label="Account number" value={data.bank.account_number} mono onCopy={copy} testid="bank-account-number" />
                  <BankRow label="IFSC" value={data.bank.ifsc} mono onCopy={copy} testid="bank-ifsc" />
                  <BankRow label="Bank" value={[data.bank.bank_name, data.bank.branch].filter(Boolean).join(" · ")} />
                </div>
                {data.bank.instructions && (
                  <div className="pt-4 mt-4 border-t border-slate-200 text-sm text-slate-600 whitespace-pre-wrap">{data.bank.instructions}</div>
                )}
              </div>
            )}

            {/* Cash section */}
            {method === "cash" && cashEnabled && (
              <div className="border border-slate-200 bg-white p-6 mt-6" data-testid="cash-details-card">
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4 inline-flex items-center gap-2">
                  <Wallet size={14} weight="duotone" /> Cash payment at the counter
                </div>
                <p className="text-sm text-slate-700">
                  Visit our office to pay in cash. Once you confirm below, staff will verify your payment in the system after you pay at the counter.
                </p>
                {data.cash.instructions && (
                  <div className="pt-4 mt-4 border-t border-slate-200 text-sm text-slate-600 whitespace-pre-wrap">{data.cash.instructions}</div>
                )}
              </div>
            )}

            {/* Submission form */}
            <form onSubmit={submit} className="border border-slate-900 bg-white p-6 mt-6 space-y-4" data-testid="mark-paid-form">
              <div>
                <div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-1">{claimed ? "Update reference" : "Confirm payment"}</div>
                <div className="font-display text-xl font-bold tracking-tight">
                  {method === "upi" && (claimed ? "Submitted the wrong UTR? Update it here." : "After you pay — submit the UTR")}
                  {method === "bank_transfer" && (claimed ? "Update bank transaction reference" : "After you transfer — submit the bank reference")}
                  {method === "cash" && "Confirm you'll pay at the counter"}
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  {method === "upi" && (claimed ? "Your last claim is awaiting verification. If you mistyped, paste the correct UTR — every attempt is logged." : "Open your UPI app, find the transaction, and copy the UTR / Reference number here.")}
                  {method === "bank_transfer" && "Enter the transaction reference / UTR returned by your bank for this NEFT / IMPS transfer."}
                  {method === "cash" && "Click below to inform the office you intend to pay in cash. Staff will mark it verified at the counter after collecting payment."}
                </p>
              </div>

              {method === "upi" && (
                <div>
                  <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">UTR / Reference number</div>
                  <input className="sms-input font-mono-sms" required minLength={6} data-testid="input-utr" value={utr} onChange={(e) => setUtr(e.target.value)} />
                </div>
              )}
              {method === "bank_transfer" && (
                <div>
                  <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Bank transaction number</div>
                  <input className="sms-input font-mono-sms" required minLength={4} data-testid="input-bank-txn" value={bankTxn} onChange={(e) => setBankTxn(e.target.value)} placeholder="e.g. NEFT N987654321" />
                </div>
              )}
              <div>
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Note (optional)</div>
                <input className="sms-input" placeholder="e.g. Paid via PhonePe at 5:30 pm" data-testid="input-payment-note" value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <button type="submit" disabled={submitting} data-testid="submit-mark-paid" className="sms-btn-primary w-full">
                {submitting
                  ? "Submitting…"
                  : method === "cash"
                  ? "Confirm — I'll pay at the counter"
                  : claimed
                  ? "Update reference"
                  : "I have paid — submit"}
              </button>
            </form>
          </>
        )}

        {/* Instalments table */}
        {installments.length > 0 && (
          <div className="border border-slate-200 bg-white mt-6" data-testid="public-installments">
            <div className="px-4 py-3 border-b border-slate-200 font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Schedule ({installments.length} instalment{installments.length === 1 ? "" : "s"})
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">
                  <th className="text-left px-4 py-2">#</th>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Due</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {installments.map((it) => (
                  <tr key={it.number} className="border-t border-slate-200">
                    <td className="px-4 py-2 font-mono-sms">{it.number}</td>
                    <td className="px-4 py-2 text-right font-mono-sms">₹{Number(it.amount).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2 font-mono-sms text-slate-500">{it.due_date}</td>
                    <td className="px-4 py-2">
                      <span className={`sms-badge ${it.status === "verified" ? "sms-badge-approved" : it.status === "claimed" ? "sms-badge-pending" : "sms-badge-rejected"}`}>{it.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}

function BankRow({ label, value, mono, onCopy, testid }) {
  if (!value) return null;
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className="flex items-center gap-2 mt-1">
        <div className={`${mono ? "font-mono-sms" : ""} text-sm text-slate-900 break-all`} data-testid={testid}>{value}</div>
        {onCopy && (
          <button type="button" onClick={() => onCopy(value)} className="text-slate-400 hover:text-[var(--sms-primary)]" aria-label={`Copy ${label}`}>
            <Copy size={14} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}
